import { type ItemSearchFieldValue, type PortalSettings, type FieldValues } from '@gorilla/portal-shared/src/schemas';
import { type ItemSearchFieldDescriptor, type SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { Box, Flex, Icon, Toggle, Text, Tooltip } from '@vibe/core';
import { Search as SearchIcon, LearnMore as LearnMoreIcon } from '@vibe/icons';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';
import { monday } from '../../services/monday';

type ItemSearchFieldProps = {
  value: ItemSearchFieldValue | undefined;
  fieldDescriptor: ItemSearchFieldDescriptor;
  portalSettings: PortalSettings;
  sectionDescriptor: SectionDescriptor;
  sectionFieldValues: FieldValues;
  onChange: (value: ItemSearchFieldValue | undefined) => void;
  clientBoardSubitemsBoardId: number | null;
};

export function ItemSearchField({
  value,
  fieldDescriptor,
  portalSettings,
  sectionDescriptor,
  sectionFieldValues,
  clientBoardSubitemsBoardId,
  onChange,
}: ItemSearchFieldProps) {
  const { board, isLoading, boardId } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues,
  });

  if (!boardId || isLoading || !board) {
    return null;
  }

  return (
    <Box
      border={true}
      borderColor="layoutBorderColor"
      className="section__field section__field--itemsearch section__field--nomargin item--actions--wrapper"
    >
      <Box backgroundColor="greyBackgroundColor">
        <Box padding="medium">
          <Flex
            gap={24}
            style={{
              height: '30px',
            }}
          >
            <Flex gap={8}>
              <Icon icon={SearchIcon} size={20} style={{ marginRight: '2px', transform: 'translate3d(1px,0,0)' }} />
              <Text type="text2" weight="bold" align="start" ellipsis={true}>
                Item search
              </Text>
              <div>
                <Tooltip
                  title="What's this?"
                  content="Allows clients to filter board items by searching the item name or a supported column. Click to learn more."
                  moveBy={{ main: 5, secondary: -15 }}
                  position="right"
                  showOnDialogEnter={false}
                >
                  <div
                    onClick={() => {
                      monday.execute('openLinkInTab', {
                        url: 'https://getgorilla.app/products/client-portal/docs/user-guides/item-search',
                      });
                    }}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <Icon icon={LearnMoreIcon} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
                  </div>
                </Tooltip>
              </div>
            </Flex>
            <Box style={{ marginLeft: 'auto' }}>
              <Flex
                justify="start"
                gap={10}
                style={{
                  flex: 1,
                }}
              >
                <Box
                  style={{
                    marginRight: 'auto',
                  }}
                >
                  <Flex style={{ margin: '0 -8px' }}>
                    <Toggle
                      offOverrideText=""
                      onOverrideText=""
                      size="small"
                      isSelected={!!value?.enabled}
                      onChange={(isSelected) => {
                        onChange({ ...(value || {}), enabled: isSelected });
                      }}
                    />
                  </Flex>
                </Box>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}
